import React, { useState } from 'react';

import {
  Button,
  Checkbox,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { WMSInfo, WMSLayerInfo, useSaveSettings } from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as z from 'zod';
import { LayerInfo, getCapabilities } from '../../WMSLayers/getCapabilities';
import {
  UpdateWmsToSettings,
  addWmsToSettings,
} from '../../WMSLayers/useWMSSettings';
import { useSettings } from '../SettingsProvider';
import { useLocalization } from '@ais-ui/locale-management';
import { cn } from '@ais-ui/utils';
import { PiGlobeHemisphereEastThin } from 'react-icons/pi';

const addWMSFormSchema = z.object({
  name: z.string().nonempty(),
  url: z.string().url().nonempty(),
  transparency: z.number().default(0),
  refreshRate: z.number().default(0),
  singleTile: z.boolean().default(false),
});

type AddWMSFormValues = z.infer<typeof addWMSFormSchema>;

const defaultValues: Partial<AddWMSFormValues> = {
  name: '',
  url: '',
  transparency: 0,
  refreshRate: 0,
  singleTile: false,
};

type CreateModeProps = {
  mode: 'create';
  existingWmsLayer?: undefined;
  className?: string;
};

type UpdateModeProps = {
  mode: 'edit';
  existingWmsLayer: WMSInfo;
  className?: string;
};

type WMSCreateUpdateServiceProps = CreateModeProps | UpdateModeProps;
type NumberInputEvent = React.ChangeEvent<HTMLInputElement>;

const layerToLayerSpecifics = (
  layers: LayerInfo[],
  allowedCoordinateSystems = ['EPSG:3857']
): WMSLayerInfo[] => {
  const layerSpecifics = layers.map((layer) => {
    // Filter coordinate systems to include only those in the allowed list
    const coordinateSystems = layer.CRS
      ? layer.CRS.filter((crs) => allowedCoordinateSystems.includes(crs))
      : [];

    // Filter bounding boxes to include only those with CRS that are in the allowed list
    const boundingBox = layer.BoundingBox
      ? layer.BoundingBox.filter((box) =>
          allowedCoordinateSystems.includes(box.crs)
        )
      : [];

    return {
      id: uuidv4(),
      title: layer.Title,
      name: layer.Name,
      coordinateSystems: coordinateSystems,
      boundingBox: boundingBox,
      enabled: false,
    };
  });

  return layerSpecifics;
};

interface ExampleWMSLayersProps {
  onChange: (url: string) => void;
}

const ExampleWMSLayers = ({ onChange }: ExampleWMSLayersProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const exampleServices = [
    {
      name: 'Terrestris',
      url: 'https://ows.terrestris.de/osm/service',
    },
    // {
    //   name: 'Weather',
    //   url: 'https://tile.openweathermap.org/map?appid=84a7a61a7e4632fb51737417382f25d3',
    // },
    {
      name: 'European Marine Observation Bathymetry',
      url: 'https://ows.emodnet-bathymetry.eu/wms',
    },
    // {
    //   name: 'European Marine Observation Seabed Habitats',
    //   url: 'http://ows.emodnet-seabedhabitats.eu/wms',
    // },
    // {
    //   name: 'European Marine Observation Physics',
    //   url: 'http://ows.emodnet-physics.eu/wms',
    // },
    {
      name: 'NowCOAST',
      url: 'https://nowcoast.noaa.gov/geoserver/wms',
    },
    {
      name: 'NowCOAST Satellite',
      url: 'https://nowcoast.noaa.gov/geoserver/satellite/wms',
    },
    {
      name: 'NowCOAST Storm Surge',
      url: 'https://nowcoast.noaa.gov/geoserver/tropical_storm_surge/wms',
    },
    {
      name: 'GEBCO',
      url: 'https://wms.gebco.net/mapserv',
    },
    {
      name: 'Vlaams instituut voor de zee Marine Regions',
      url: 'https://geo.vliz.be/geoserver/MarineRegions/wms',
    },
    {
      name: 'Human Activities EMODNET',
      url: 'https://ows.emodnet-humanactivities.eu/wms',
    },
    // {
    //   name: 'Marine Cadastre',
    //   url: 'https://marinecadastre.gov/geoserver/wms',
    // },
    // {
    //   name: 'Copernicus',
    //   url: 'http://nrt.cmems-du.eu/thredds/wms/global-analysis-forecast-phy-001-024',
    // },
    // {
    //   name: 'UNAVCO',
    //   url: 'https://geon.unavco.org/data/wms',
    // },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" size="icon" variant="outline">
          <PiGlobeHemisphereEastThin className="h-6 w-6"></PiGlobeHemisphereEastThin>
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <Command className="space-y-2">
          <CommandInput placeholder="Find WMS..." />
          <CommandList className="space-y-1">
            <CommandGroup heading="Web Map Services">
              {exampleServices.map((service) => (
                <CommandItem
                  key={service.name}
                  onSelect={() => {
                    onChange(service.url);
                    setOpen(false);
                  }}
                >
                  {service.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const WMSAddEditService = (props: WMSCreateUpdateServiceProps) => {
  const { messages } = useLocalization();
  const { mode, existingWmsLayer } = props;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  let initialValues = defaultValues;
  if (mode === 'edit') {
    initialValues = {
      name: existingWmsLayer.name,
      url: existingWmsLayer.url,
      transparency:
        existingWmsLayer.transparency || existingWmsLayer.trans || 0,
      refreshRate: existingWmsLayer.refreshrate,
      singleTile:
        existingWmsLayer.singleTile || existingWmsLayer.singletile || false,
    };
  }

  const form = useForm<AddWMSFormValues>({
    resolver: zodResolver(addWMSFormSchema),
    defaultValues: initialValues,
  });

  const { toast } = useToast();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();

  const handleNumberChange = (
    e: NumberInputEvent,
    onChange: (value: number) => void
  ): void => {
    if (!isNaN(e.target.valueAsNumber)) {
      onChange(e.target.valueAsNumber);
    }
    form.clearErrors();
  };

  async function onUpdateService(data: AddWMSFormValues) {
    if (mode !== 'edit') {
      return;
    }
    setIsSubmitting(true);
    try {
      if (existingWmsLayer.url !== data.url) {
        const { layers, version } = await getCapabilities(data.url);
        const layerSpecifics = layerToLayerSpecifics(layers);

        existingWmsLayer.layers = layerSpecifics;
        existingWmsLayer.version = version;
      }

      existingWmsLayer.singleTile = data.singleTile;
      existingWmsLayer.transparency = data.transparency;
      existingWmsLayer.refreshrate = data.refreshRate;
      existingWmsLayer.name = data.name;
      existingWmsLayer.url = data.url;

      const settingsToSave = UpdateWmsToSettings(existingWmsLayer, settings);
      mutateSettings(settingsToSave, {
        onSuccess: () => {
          setShowDialog(false);
          toast({
            className: 'update-wms-service-success-toast',
            title: messages.genericToastTitleSuccess,
            description: messages.settingsWmsSuccessUpdateMessage,
          });
          setIsSubmitting(false);
        },
        onError: () => {
          setShowDialog(false);
          toast({
            className: 'update-wms-layer-settings-error-toast',
            title: messages.genericToastTitleError,
            description: messages.settingsWmsFailedUpdateMessage,
            variant: 'destructive',
          });
          setIsSubmitting(false);
        },
      });
    } catch (error) {
      setShowDialog(false);
      setIsSubmitting(false);
      toast({
        className: 'update-wms-service-unsuccess-toast',
        title: messages.genericToastTitleError,
        description: messages.settingsWmsFailedToFetchCapabilitiesMessage,
        variant: 'destructive',
      });
    }
  }

  async function onAddService(data: AddWMSFormValues) {
    if (mode !== 'create') {
      return;
    }

    setIsSubmitting(true);
    try {
      const { layers, version } = await getCapabilities(data.url);
      const layerSpecifics = layerToLayerSpecifics(layers);

      const wmsLayer = {
        id: uuidv4(),
        name: data.name,
        url: data.url,
        transparency: data.transparency,
        refreshrate: data.refreshRate,
        singleTile: data.singleTile,
        enabled: true,
        layers: layerSpecifics,
        selected: [],
        exception: '',
        patch: null,
        version: version,
      };

      const settingsToSave = addWmsToSettings(wmsLayer, settings);

      mutateSettings(settingsToSave, {
        onSuccess: () => {
          setShowDialog(false);
          toast({
            className: 'add-wms-service-success-toast',
            title: messages.genericToastTitleSuccess,
            description: messages.settingsWmsSuccessAddMessage,
          });
          setIsSubmitting(false);
        },
        onError: (error) => {
          setShowDialog(false);
          toast({
            className: 'set-wms-layer-settings-error-toast',
            title: messages.genericToastTitleError,
            description: messages.settingsWmsFailedAddMessage,
            variant: 'destructive',
          });
          setIsSubmitting(false);
        },
      });
    } catch (error) {
      setShowDialog(false);
      setIsSubmitting(false);

      toast({
        className: 'add-wms-service-unsuccess-toast',
        title: messages.genericToastTitleError,
        description: messages.settingsWmsFailedToFetchCapabilitiesMessage,
        variant: 'destructive',
      });
    }
  }

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        <Button
          variant={mode === 'edit' ? 'secondary' : 'default'}
          className={cn(mode === 'edit' && 'text-primary', props.className)}
        >
          {mode === 'create'
            ? messages.settingsWmsAddButtonText
            : messages.settingsWmsEditButtonText}
        </Button>
      </DialogTrigger>
      <DialogContent id="add-update-service-dialog">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? messages.settingsWmsAddTitleText
              : messages.settingsWmsEditTitleText}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? messages.settingsWmsAddSubtitleText
              : messages.settingsWmsEditSubtitleText}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={
              mode === 'create'
                ? form.handleSubmit(onAddService)
                : form.handleSubmit(onUpdateService)
            }
            className="space-y-8"
          >
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{messages.settingsWmsNameLabel} *</FormLabel>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Input
                        type="text"
                        placeholder={messages.settingsWmsNamePlaceholder}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{messages.settingsWmsUrlLabel} *</FormLabel>
                    <FormControl onChange={() => form.clearErrors()}>
                      <div className="flex justify-between space-x-4">
                        <Input
                          type="text"
                          placeholder={messages.settingsWmsUrlPlaceholder}
                          {...field}
                        />
                        <ExampleWMSLayers onChange={field.onChange} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex space-x-4">
                <FormField
                  control={form.control}
                  name="transparency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {messages.settingsWmsTransparencyLabel}
                      </FormLabel>
                      <FormControl
                        onChange={(e: NumberInputEvent) =>
                          handleNumberChange(e, field.onChange)
                        }
                      >
                        <Input
                          type="number"
                          placeholder={
                            messages.settingsWmsTransparencyPlaceholder
                          }
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="refreshRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {messages.settingsWmsRefreshRateLabel}
                      </FormLabel>
                      <FormControl
                        onChange={(e: NumberInputEvent) =>
                          handleNumberChange(e, field.onChange)
                        }
                      >
                        <Input
                          type="number"
                          placeholder={
                            messages.settingsWmsRefreshRatePlaceholder
                          }
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <DialogFooter className="p-4">
              <Button
                id="wms-confirm-add-button"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? messages.genericSubmitting
                  : messages.genericSubmit}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
